"use client";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Link from 'next/link';
import { useParams } from 'next/navigation';

// --- Constants & Types ---
const COLS = 10;
const ROWS = 20;

type Grid = (string | null)[][];

const SHAPES = {
  I: [[1, 1, 1, 1]],
  J: [
    [1, 0, 0],
    [1, 1, 1],
  ],
  L: [
    [0, 0, 1],
    [1, 1, 1],
  ],
  O: [
    [1, 1],
    [1, 1],
  ],
  S: [
    [0, 1, 1],
    [1, 1, 0],
  ],
  T: [
    [0, 1, 0],
    [1, 1, 1],
  ],
  Z: [
    [1, 1, 0],
    [0, 1, 1],
  ],
  // Empty shape as fallback
  EMPTY: [[0]]
};

const COLORS: Record<string, string> = {
  I: "bg-cyan-400",
  J: "bg-blue-500",
  L: "bg-orange-500",
  O: "bg-yellow-400",
  S: "bg-green-500",
  T: "bg-purple-500",
  Z: "bg-red-500",
};

const RANDOM_SHAPES = ["I", "J", "L", "O", "S", "T", "Z"];

function createGrid(): Grid {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(null));
}

// --- Helpers ---
const shuffle = (array: string[]) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
};

// Bag Generator
let bag: string[] = [];
const getNextPieceFromBag = () => {
    if (bag.length === 0) {
        bag = shuffle([...RANDOM_SHAPES]);
    }
    const type = bag.pop()!;
    const shape = SHAPES[type as keyof typeof SHAPES];
    return {
        shape,
        type,
        x: Math.floor(COLS / 2) - Math.floor(shape[0].length / 2),
        y: 0,
    };
};

const getRandomPiece = () => {
  return getNextPieceFromBag();
};

const checkCollision = (
  piece: { shape: number[][]; x: number; y: number },
  board: Grid
) => {
  if (!piece) return false;
  for (let y = 0; y < piece.shape.length; y++) {
    for (let x = 0; x < piece.shape[y].length; x++) {
      if (piece.shape[y][x] !== 0) {
        const newY = piece.y + y;
        const newX = piece.x + x;

        if (
          newX < 0 ||
          newX >= COLS ||
          newY >= ROWS ||
          (newY >= 0 && board[newY][newX] !== null)
        ) {
          return true;
        }
      }
    }
  }
  return false;
};

// --- Component ---
const TRANSLATIONS = {
  en: {
    score: "Score",
    level: "Level",
    hold: "Hold",
    next: "Next",
    nextUp: "Next Up",
    controls: "Controls",
    rotate: "Rotate",
    move: "Move",
    softDrop: "Soft Drop",
    hardDrop: "Hard Drop",
    restart: "Restart",
    startGame: "START GAME",
    gameOver: "GAME OVER",
    retry: "Retry",
    home: "Home",
    empty: "Empty"
  },
  de: {
    score: "Punkte",
    level: "Level",
    hold: "Halten",
    next: "Nächster",
    nextUp: "Nächster",
    controls: "Steuerung",
    rotate: "Drehen",
    move: "Bewegen",
    softDrop: "Sanfter Fall",
    hardDrop: "Harter Fall",
    restart: "Neustart",
    startGame: "STARTEN",
    gameOver: "SPIEL VORBEI",
    retry: "Nochmal",
    home: "Startseite",
    empty: "Leer"
  }
};

const Tetris = () => {
  // Game State
  const [grid, setGrid] = useState<Grid>(createGrid());
  const [currentPiece, setCurrentPiece] = useState<{
    shape: number[][];
    type: string;
    x: number;
    y: number;
  } | null>(null);
  
  // Next & Hold State
  const [nextPieces, setNextPieces] = useState<ReturnType<typeof getRandomPiece>[]>([]);
  const [holdPiece, setHoldPiece] = useState<{ shape: number[][], type: string } | null>(null);
  const [canHold, setCanHold] = useState(true);

  // Status State
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1000);

  // Derived Level
  const level = Math.floor(lines / 10);
  const params = useParams();
  const lang = (params?.lang as 'en' | 'de') || 'en';
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  const requestRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  
  // Ref for avoiding stale state
  const stateRef = useRef({
      grid,
      currentPiece,
      gameOver,
      isPlaying,
      nextPieces,
      canHold,
      holdPiece,
      score,
      lines,
      level
  });
  
  // Sync Ref
  useEffect(() => {
    stateRef.current = { grid, currentPiece, gameOver, isPlaying, nextPieces, canHold, holdPiece, score, lines, level };
  }, [grid, currentPiece, gameOver, isPlaying, nextPieces, canHold, holdPiece, score, lines, level]);

  // --- Logic ---

  const spawnNextPiece = useCallback(() => {
     // Use stateRef to get the current queue without depending on stale closures or impure updaters
     const currentQueue = stateRef.current.nextPieces;
     const next = [...currentQueue];

     // Ensure we have enough pieces
     while(next.length <= 4) {
          next.push(getRandomPiece());
     }
     
     const pieceToSpawn = next.shift()!;
     // Center the new piece
     pieceToSpawn.x = Math.floor(COLS / 2) - Math.floor(pieceToSpawn.shape[0].length / 2);
     pieceToSpawn.y = 0;

     // Batched updates
     setCurrentPiece(pieceToSpawn);
     setNextPieces(next);
     setCanHold(true);
  }, []);

  const startGame = useCallback(() => {
    setGrid(createGrid());
    setScore(0);
    setLines(0);
    setGameOver(false);
    setIsPlaying(true);
    setSpeed(1000);
    setHoldPiece(null);
    setCanHold(true);

    const initialQueue = [getRandomPiece(), getRandomPiece(), getRandomPiece(), getRandomPiece(), getRandomPiece()];
    const firstPiece = initialQueue.shift()!;
    setCurrentPiece(firstPiece);
    setNextPieces(initialQueue);    
  }, []);
  
  useEffect(() => { startGame(); }, [startGame]);
  
  const lockPiece = useCallback((pieceToLock?: { shape: number[][], type: string, x: number, y: number }) => {
    // Always prefer explicit piece, then Ref (latest), then State (fallback)
    const current = pieceToLock || stateRef.current.currentPiece; 
    if (!current) return;

    setGrid((prev) => {
      // We start from 'prev' which is the latest grid from React state update queue.
      const newGrid = prev.map((row) => [...row]);
      
      // Place piece
      current.shape.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value !== 0) {
            const newY = current.y + y;
            const newX = current.x + x;
            if (newY >= 0 && newY < ROWS && newX >= 0 && newX < COLS) {
               if(newGrid[newY]) newGrid[newY][newX] = current.type;
            }
          }
        });
      });

      const remainingRows = newGrid.filter((row) => row.some((cell) => cell === null));
      while (remainingRows.length < ROWS) {
        remainingRows.unshift(Array(COLS).fill(null));
      }
      return remainingRows;
    });
    
    // SCORE CALCULATION (Simulation with Ref to avoid async issues)
    const prevGrid = stateRef.current.grid;
    const newGrid = prevGrid.map((row) => [...row]);
    current.shape.forEach((row, y) => {
        row.forEach((value, x) => {
          if (value !== 0) {
             const newY = current.y + y;
             const newX = current.x + x;
             if (newY >= 0 && newY < ROWS && newX >= 0 && newX < COLS) {
               if(newGrid[newY]) newGrid[newY][newX] = current.type;
             }
          }
        });
    });
    const remainingRows = newGrid.filter((row) => row.some((cell) => cell === null));
    const clearedCount = ROWS - remainingRows.length;
    
    if (clearedCount > 0) {
        // Nintendo Scoring System: (Base * (Level + 1))
        // Base: 40, 100, 300, 1200
        const startLevel = Math.floor(stateRef.current.lines / 10);
        const basePoints = [0, 40, 100, 300, 1200];
        // Ensure index is within bounds (though clearedCount is naturally <= 4)
        const points = (basePoints[clearedCount] || 0) * (startLevel + 1);
        
        setLines(l => l + clearedCount);
        setScore(s => s + points);
        
        // Update speed based on NEW level
        const newTotalLines = stateRef.current.lines + clearedCount;
        const newLevel = Math.floor(newTotalLines / 10);
        
        // SMOOTHER CURVE & PLAYABLE HARD LIMIT: 
        // Cap speed at 120ms (challenging but playable limit), scale down by 15% every level.
        setSpeed(Math.max(120, Math.floor(1000 * Math.pow(0.85, newLevel)))); 
    }

    spawnNextPiece();
  }, [spawnNextPiece]);


  // Check Game Over on spawn
  useEffect(() => {
      // Use Ref to check collision against *latest* grid
      const curr = stateRef.current.currentPiece;
      const gr = stateRef.current.grid;
      if (!curr || !stateRef.current.isPlaying) return;
      
      if (checkCollision(curr, gr)) {
          setGameOver(true);
          setIsPlaying(false);
      }
  }, [currentPiece, isPlaying]);


  const move = useCallback(
    (dirX: number, dirY: number, isSoftDrop = false) => {
      // Use Ref to ensure we aren't using stale 'currentPiece' from a previous closure
      const curr = stateRef.current.currentPiece;
      const gr = stateRef.current.grid;
      const over = stateRef.current.gameOver;
      
      if (!curr || over) return;

      const nextPos = { ...curr, x: curr.x + dirX, y: curr.y + dirY };

      if (!checkCollision(nextPos, gr)) {
        setCurrentPiece(nextPos);
        if (isSoftDrop) {
            setScore(s => s + 1); // Soft Drop awards 1pt per cell
        }
      } else if (dirY > 0) {
        // Lock only if moving down blocked
        lockPiece();
      }
    },
    [lockPiece] // Removed 'currentPiece', 'grid' deps to prove we rely on Ref
  );

  const rotate = useCallback(() => {
    const curr = stateRef.current.currentPiece;
    const gr = stateRef.current.grid;
    const over = stateRef.current.gameOver;
    
    if (!curr || over) return;
    
    const rotatedShape = curr.shape[0].map((_, index) => curr.shape.map((row) => row[index]).reverse());
    const nextPos = { ...curr, shape: rotatedShape };

    if (!checkCollision(nextPos, gr)) {
      setCurrentPiece(nextPos);
    } else {
      // Wall kicks
      const offsetPos = { ...nextPos, x: nextPos.x - 1 };
      if (!checkCollision(offsetPos, gr)) {
        setCurrentPiece(offsetPos);
        return;
      }
      const offsetPos2 = { ...nextPos, x: nextPos.x + 1 };
      if (!checkCollision(offsetPos2, gr)) {
        setCurrentPiece(offsetPos2);
        return;
      }
    }
  }, []); // Empty deps - uses Ref

  // Hold Function
  const hold = useCallback(() => {
      const curr = stateRef.current.currentPiece;
      const can = stateRef.current.canHold;
      const over = stateRef.current.gameOver;
      const playing = stateRef.current.isPlaying;
      const held = stateRef.current.holdPiece;

      if (!curr || !can || over || !playing) return;
      
      const pieceData = { shape: SHAPES[curr.type as keyof typeof SHAPES] || curr.shape, type: curr.type }; 
      setCanHold(false);
      if (held) {
          setHoldPiece(pieceData);
          setCurrentPiece({
              shape: held.shape,
              type: held.type,
              x: Math.floor(COLS / 2) - Math.floor(held.shape[0].length / 2),
              y: 0
          });
      } else {
          setHoldPiece(pieceData);
          spawnNextPiece();
      }
  }, [spawnNextPiece]);

  const triggerHardDrop = () => {
      const { currentPiece, grid } = stateRef.current;
      if (!currentPiece) return;
      let newY = currentPiece.y;
      while (!checkCollision({...currentPiece, y: newY + 1}, grid)) {
          newY++;
      }
      // Calculate drop distance to award 2 points per dropped row
      const dropDistance = newY - currentPiece.y;
      setScore(s => s + (dropDistance * 2));
      lockPiece({ ...currentPiece, y: newY });
  };

  // Input
  const keyHoldRef = useRef(0);
  const [isSoftDropping, setIsSoftDropping] = useState(false);
  const isSoftDroppingRef = useRef(false);

  useEffect(() => {
      isSoftDroppingRef.current = isSoftDropping;
  }, [isSoftDropping]);

  // --- Game Loop ---
  useEffect(() => {
    if (!isPlaying || gameOver) {
      cancelAnimationFrame(requestRef.current);
      return;
    }
    const animate = (time: number) => {
      // If soft dropping, use strict 50ms interval. Otherwise use level-based speed.
      const currentSpeed = isSoftDroppingRef.current ? 50 : speed;
      
      if (time - lastTimeRef.current > currentSpeed) {
        move(0, 1, isSoftDroppingRef.current); // Pass true if soft dropping to award points logic
        lastTimeRef.current = time;
      }
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [isPlaying, gameOver, speed, move]); // Removed isSoftDropping dependency to avoid loop reset flickering
  // Note: We use ref inside loop to access latest value without resetting the loop

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isPlaying || gameOver) {
        if (e.key === 'Enter') startGame();
        if (e.key === ' ' && gameOver) e.preventDefault();
        return;
      }
      
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", " ", "c", "C", "Shift"].includes(e.key)) e.preventDefault();

      switch (e.key) {
        case "ArrowLeft": move(-1, 0); break;
        case "ArrowRight": move(1, 0); break;
        case "ArrowDown": if (!isSoftDroppingRef.current) setIsSoftDropping(true); break; // Soft Drop Start
        case "ArrowUp": rotate(); break;
        case "c": 
        case "C":
        case "Shift": hold(); break;
        case " ": triggerHardDrop(); break;
        case "r":
        case "R":
            if (!keyHoldRef.current) {
                keyHoldRef.current = window.setTimeout(() => {
                    startGame();
                }, 300);
            }
            break;
      }
    };
    const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key === "ArrowDown") {
            setIsSoftDropping(false);
        }
        if (e.key === "r" || e.key === "R") {
            if (keyHoldRef.current) {
                clearTimeout(keyHoldRef.current);
                keyHoldRef.current = 0;
            }
        }
    };
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
        window.removeEventListener("keydown", handleKeyDown);
        window.removeEventListener("keyup", handleKeyUp);
    };
  }, [isPlaying, gameOver, move, rotate, hold, startGame]); 

  // Touch
  const [touchStart, setTouchStart] = useState<{ x: number, y: number } | null>(null);
  const handleTouchStart = (e: React.TouchEvent) => setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  const handleTouchEnd = (e: React.TouchEvent) => {
      setIsSoftDropping(false); // End soft drop if finger lifted
      if (!touchStart) return;
      // Keeping existing logic for swipe:
      const dx = touchStart.x - e.changedTouches[0].clientX;
      const dy = touchStart.y - e.changedTouches[0].clientY;
      if (Math.abs(dx) > Math.abs(dy)) {
          if (Math.abs(dx) > 30) move(dx > 0 ? -1 : 1, 0);
      } else {
          if (Math.abs(dy) > 30) {
              if (dy > 0) rotate(); else triggerHardDrop(); 
          }
      }
      setTouchStart(null);
  };

  // --- Render Helpers ---
  const renderMiniGrid = (piece: { shape: number[][], type: string } | null, label: string, compact = false) => (
      <div className={`flex flex-col items-center gap-1 ${compact ? '' : 'mb-2'} w-full`}>
          {label && <span className="text-neutral-600 dark:text-neutral-400 text-[10px] font-bold uppercase tracking-widest">{label}</span>}
          <div className={`bg-beige dark:bg-neutral-900/50 p-1 rounded-lg border border-black/30 dark:border-neutral-800 flex items-center justify-center ${compact ? 'w-10 h-10' : 'w-16 h-16 xl:w-20 xl:h-20'}`}>
              {piece ? (
                  <div className="grid gap-0" style={{ gridTemplateColumns: `repeat(${piece.shape[0].length}, 1fr)` }}>
                      {piece.shape.map((row, y) => row.map((val, x) => (
                          val ? <div key={`${x}-${y}`} className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2 xl:w-3 xl:h-3'} ${COLORS[piece.type]} ring-1 ring-black/20 dark:ring-black/40`} /> : (
                             <div key={`${x}-${y}`} className={`${compact ? 'w-1.5 h-1.5' : 'w-2 h-2 xl:w-3 xl:h-3'} opacity-0`} />
                          )
                      )))}
                  </div>
              ) : <span className="text-neutral-600 dark:text-neutral-400 text-[10px] md:text-xs italic">{t.empty}</span>}
          </div>
      </div>
  );

  const displayGrid = grid.map((row) => [...row]);
  
  // 1. Ghost Piece Calculation & Rendering
  let ghostPiece = null;
  if (currentPiece && isPlaying && !gameOver) {
      // Clone current piece to simulate drop
      ghostPiece = { ...currentPiece };
      // Move down until collision
      while (!checkCollision({ ...ghostPiece, y: ghostPiece.y + 1 }, grid)) {
          ghostPiece.y++;
      }
      
      // Render Ghost Piece (if it's not in the same position as current piece)
      // Actually, standard is to always render it, but behind current.
      // We render it first so current piece overlays it if they overlap.
      ghostPiece.shape.forEach((row, y) => {
          row.forEach((value, x) => {
              if (value !== 0) {
                  const newY = ghostPiece!.y + y;
                  const newX = ghostPiece!.x + x;
                  if (newY >= 0 && newY < ROWS && newX >= 0 && newX < COLS) {
                      // Only draw if cell is empty (don't overwrite placed blocks, though collision check prevents that)
                      if (!displayGrid[newY][newX]) { 
                          displayGrid[newY][newX] = `${currentPiece.type}-ghost`; // Special marker
                      }
                  }
              }
          });
      });
  }

  // 2. Current Piece Rendering
  if (currentPiece) {
    currentPiece.shape.forEach((row, y) => {
      row.forEach((value, x) => {
        if (value !== 0) {
          const newY = currentPiece.y + y;
          const newX = currentPiece.x + x;
          if (newY >= 0 && newY < ROWS && newX >= 0 && newX < COLS) {
            displayGrid[newY][newX] = currentPiece.type;
          }
        }
      });
    });
  }

  const handleMobileBtn = (action: () => void) => (e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      action();
  };

  return (
    <div className="relative h-full flex flex-col xl:flex-row items-center justify-around xl:justify-center xl:gap-10 pt-20 md:pt-28 w-full pb-8 xl:pb-8 overflow-hidden select-none touch-none"
         onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
       
       {/* Mobile Top Bar: Level | Score */}
       <div className="xl:hidden w-full flex justify-center px-6 shrink-0 z-20">
             <div className="flex flex-col items-center">
                 <span className="text-[10px] text-neutral-600 dark:text-neutral-400 font-bold uppercase tracking-widest mb-1">{t.score}</span>
                 <span className="text-green font-bold text-xl font-mono leading-none">{level} | {score.toLocaleString()}</span>
             </div>
       </div>

       {/* Left Column: HOLD & Legend (Desktop) */}
       <div className="w-full xl:w-35 flex-row xl:flex-col justify-between xl:justify-between items-center xl:items-end gap-x-8 gap-y-12 xl:order-1 px-8 xl:px-0 hidden xl:flex h-full py-8">
             {/* HOLD */}
             <div className="flex flex-col items-center">
                 {renderMiniGrid(holdPiece, t.hold)}
             </div>
             
             {/* Desktop Legend */}
             <div className="hidden xl:block w-full text-right mt-auto">
                 <h3 className="text-black dark:text-white font-bold text-xs uppercase tracking-widest text-start mb-4 border-b border-black/10 dark:border-white/10 pb-2">{t.controls}</h3>
                 <div className="grid grid-cols-2 gap-y-3 text-xs">
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.rotate}</div>
                     <div className="text-right text-green font-mono">↑</div>
                     
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.move}</div>
                     <div className="text-right text-green font-mono">← →</div>
                     
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.softDrop}</div>
                     <div className="text-right text-green font-mono">↓</div>
                     
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.hardDrop}</div>
                     <div className="text-right text-green font-mono">Space</div>
                     
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.hold}</div>
                     <div className="text-right text-green font-mono">C</div>
                     
                     <div className="text-neutral-600 dark:text-neutral-400 text-left">{t.restart}</div>
                     <div className="text-right text-green font-mono">Hold R</div>
                 </div>
             </div>
       </div>

       {/* Center: BOARD */}
       <div className="xl:order-2 flex flex-col items-center justify-center gap-2 z-10 w-full max-w-[374px] xl:max-w-none xl:w-auto shrink min-h-0">
          <div className="relative flex justify-center items-start gap-4">
              {/* Desktop Score Badge */}
              <div className="hidden xl:flex absolute -top-14 left-1/2 -translate-x-1/2 flex-col items-center gap-1 min-w-[200px] z-20">
                  <span className="text-[10px] text-neutral-600 dark:text-neutral-400 font-bold uppercase tracking-widest">{t.score} / {t.level} {level}</span>
                  <span className="text-green font-bold text-2xl font-mono tracking-tighter shadow-black/10 dark:shadow-black/50 drop-shadow-md">{score.toLocaleString()}</span>
              </div>
              
              <div className="relative bg-beige-dark dark:bg-neutral-900 border-4 xl:border-6 border-neutral-800 rounded-xl xl:rounded-2xl p-1 shadow-[0_0_20px_0_rgba(0,0,0,0.1)] dark:shadow-[0_0_20px_0_rgba(252,244,225,0.1)] ring-1 ring-black/5 dark:ring-white/5">
                <div className="h-[45dvh] w-auto aspect-1/2 max-h-[400px] md:max-h-[600px] xl:h-[80dvh] xl:max-h-none xl:w-auto grid grid-cols-10 grid-rows-20 gap-0 border-t border-l border-black/10 dark:border-neutral-800/80 bg-beige-dark dark:bg-neutral-950">
                    {displayGrid.flat().map((cell, i) => {
                        const isGhost = cell && cell.endsWith("-ghost");
                        const cellType = cell && isGhost ? cell.replace("-ghost", "") : cell;
                        
                        return (
                        <div
                            key={i}
                            className={`w-full h-full box-border border-r border-b border-black/10 dark:border-neutral-800/80 ${
                            cell 
                            ? (cellType ? COLORS[cellType] : "bg-beige-dark dark:bg-neutral-900/80")
                            : "bg-beige-dark dark:bg-neutral-900/80"
                        } ${!isGhost && cell ? 'shadow-[inset_0_2px_4px_rgba(255,255,255,0.2),inset_0_-2px_4px_rgba(0,0,0,0.2)] rounded-[1px]' : ''} ${isGhost ? 'opacity-25 dark:opacity-40' : ''}`}
                        />
                    )})}
                </div>

                {/* Overlays */}
                {!isPlaying && !gameOver && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/80 backdrop-blur-sm z-10 rounded-lg">
                    <button onClick={startGame} className="group relative px-8 py-4 bg-white text-black font-bold rounded-full overflow-hidden transition-transform hover:scale-105">
                        <span className="relative z-10">{t.startGame}</span>
                        <div className="absolute inset-0 bg-green transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 z-0 opacity-20"/>
                    </button>
                </div>
                )}
                {gameOver && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-beige-dark dark:bg-black/95 backdrop-blur-md z-10 p-6 md:p-8 text-center rounded-lg">
                    <h2 className="text-3xl md:text-4xl font-black text-green mb-1 md:mb-2">{t.gameOver}</h2>
                    <div className="text-xs md:text-sm mb-4 md:mb-6 uppercase tracking-widest text-neutral-600 dark:text-neutral-400">{t.level} {level}</div>
                    <div className="text-black dark:text-white text-3xl md:text-5xl font-mono mb-6 md:mb-8">{score.toLocaleString()}</div>
                    <div className="flex flex-col gap-2 md:gap-3 w-full max-w-[180px] md:max-w-[200px]">
                        <button onClick={startGame} className="w-full px-5 md:px-8 py-3 bg-green border-green border-2 text-white hover:text-green font-bold rounded-full hover:bg-beige hover:dark:bg-black transition-colors duration-300 shadow-lg">
                          {t.retry}
                        </button>
                        <Link href={`/${lang}`} className="w-full px-5 md:px-8 py-3 bg-red-950/30 text-red-600 dark:text-red-400 font-bold rounded-full hover:bg-red-600 hover:text-white transition-colors duration-300 shadow-lg block">
                          {t.home}
                        </Link>
                    </div>
                </div>
                )}
            </div>
          </div>
       </div>

       {/* Mobile Hold & Next (Centered Row) */}
       <div className="xl:hidden w-full flex justify-center items-center gap-4 shrink-0 py-1">
            <div className="flex flex-col items-center">
                <span className="text-neutral-600 dark:text-neutral-400 text-[10px] font-bold uppercase tracking-widest mb-1">{t.hold}</span>
                {renderMiniGrid(holdPiece, "", true)}
            </div>
            <div className="flex flex-col items-center">
                <span className="text-neutral-600 dark:text-neutral-400 text-[10px] font-bold uppercase tracking-widest mb-1">{t.next}</span>
                <div className="flex gap-2">
                    {nextPieces.slice(0, 3).map((p, i) => (
                        <div key={i}>
                            {renderMiniGrid(p, "", true)}
                        </div>
                    ))}
                </div>
            </div>
       </div>

        {/* Mobile Controls UI - Buttons only, no text changes needed except labels logic */}
       <div className="xl:hidden w-full px-4 flex flex-col gap-4 mt-1 shrink-0 pb-0">
                <div className="flex justify-between items-center w-full max-w-[320px] mx-auto gap-2">
                    {/* D-Pad Buttons... (SVG icons, no text) */}
                    <div className="grid grid-cols-3 gap-2">
                        <button 
                            className="size-12 bg-beige-dark dark:bg-neutral-800 rounded-full flex items-center justify-center active:bg-neutral-700 active:scale-95 transition-all border border-neutral-700 shadow-lg touch-none select-none"
                            onPointerDown={handleMobileBtn(() => move(-1, 0))}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-beige pointer-events-none">
                                <path d="m12 19-7-7 7-7"/>
                                <path d="M19 12H5"/>
                            </svg>
                        </button>
                        <button 
                            className="size-12 bg-beige-dark dark:bg-neutral-800 rounded-full flex items-center justify-center active:bg-neutral-700 active:scale-95 transition-all border border-neutral-700 shadow-lg touch-none select-none"
                            onPointerDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsSoftDropping(true);
                            }}
                            onPointerUp={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsSoftDropping(false);
                            }}
                            onPointerLeave={() => setIsSoftDropping(false)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-beige pointer-events-none">
                                <path d="M12 5v14"/>
                                <path d="m19 12-7 7-7-7"/>
                            </svg>
                        </button>
                        <button 
                            className="size-12 bg-beige-dark dark:bg-neutral-800 rounded-full flex items-center justify-center active:bg-neutral-700 active:scale-95 transition-all border border-neutral-700 shadow-lg touch-none select-none"
                            onPointerDown={handleMobileBtn(() => move(1, 0))}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black dark:text-beige pointer-events-none">
                                <path d="M5 12h14"/>
                                <path d="m12 5 7 7-7 7"/>
                            </svg>
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-4">
                            <button 
                                className="size-12 bg-green rounded-full flex items-center justify-center active:bg-green/80 active:scale-95 transition-all shadow-lg shadow-green/20 touch-none select-none"
                                onPointerDown={handleMobileBtn(() => rotate())}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-black pointer-events-none">
                                    <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                                    <path d="M21 3v5h-5" />
                                </svg>
                            </button>
                            <button 
                                className="size-12 bg-beige-dark dark:bg-neutral-800 border-2 border-green rounded-full flex items-center justify-center active:bg-neutral-700 active:scale-95 transition-all shadow-lg text-green touch-none select-none"
                                onPointerDown={handleMobileBtn(() => triggerHardDrop())}
                            >
                                <span className="font-bold text-xs pointer-events-none">DROP</span>
                            </button>
                        </div>
                    </div>
                </div>
                
                {/* Hold / Restart Small Row */}
                <div className="flex justify-center gap-2 mt-1">
                    <button 
                        onPointerDown={handleMobileBtn(() => hold())}
                        className="text-xs text-black dark:text-beige font-bold uppercase tracking-widest px-6 py-3 bg-beige-dark dark:bg-neutral-800 rounded-full border border-neutral-800 active:bg-neutral-800 touch-none select-none"
                    >
                        {t.hold} (C)
                    </button>
                    <button onClick={startGame} className="text-xs text-black dark:text-beige font-bold uppercase tracking-widest px-6 py-3 bg-beige-dark dark:bg-neutral-800 rounded-full border border-neutral-800 active:bg-neutral-800">
                        {t.restart}
                    </button>
                </div>
          </div>

       {/* Right Column: NEXT */}

       <div className="h-full xl:h-auto w-full xl:w-48 flex-col items-center xl:items-start gap-8 order-3 px-8 xl:px-0 mt-8 xl:mt-0 hidden xl:flex">
           <div className="w-full xl:w-fit max-w-[200px] xl:max-w-none">
               <span className="text-neutral-600 dark:text-neutral-400 text-[10px] font-bold uppercase tracking-widest mb-4 block text-center">{t.nextUp}</span>
               <div className="flex flex-row xl:flex-col gap-4 justify-center items-center">
                   {nextPieces.slice(0, 3).map((p, i) => (
                       <div key={i} className="scale-90 xl:scale-100">
                           {renderMiniGrid(p, "")}
                       </div>
                   ))}
               </div>
           </div>
       </div>

      {/* Mobile legal Links */}
      <div className="absolute bottom-1 right-0 z-20 flex gap-4 xl:hidden">
        <Link 
          href={`/${lang}/data-privacy`} 
          className="duration-300 text-[16px] text-neutral-600 dark:text-neutral-400 hover:text-black hover:dark:text-white"
        >
          {lang === 'de' ? 'Datenschutz' : 'Data Privacy'}
        </Link>

        <Link 
          href={`/${lang}/imprint`} 
          className="duration-300 text-[16px] text-neutral-600 dark:text-neutral-400 hover:text-black hover:dark:text-white"
        >
          {lang === 'de' ? 'Impressum' : 'Imprint'}
        </Link>
      </div>

    </div>
  );
};

export default Tetris;